import { apiRoutes } from '@/constants/api-routes';
import { apiRequest } from './http';
import type { PromptView } from '@/types/api';

/**
 * Payload for creating a new prompt under a book. Matches the server's
 * `CreatePromptDto` — `prompt` is the question text; the server trims it
 * and leaves `response` null until `/learn` answers it.
 */
export interface CreatePromptPayload {
  prompt: string;
}

/**
 * Payload for `PATCH /prompts/:id`. Matches the server's `UpdatePromptDto`.
 * The server doesn't clear the cached response on an edit — if semantics
 * change enough to invalidate the answer, delete and recreate.
 */
export interface UpdatePromptPayload {
  prompt?: string;
}

/**
 * Prompts wrapper.
 *
 * - `GET /books/:bookId/prompts` is open to any authenticated user (the
 *   book detail surface shows suggested prompts to readers).
 * - `POST`, `PATCH`, `DELETE` are admin-gated at the API layer; the client
 *   still exposes them here because the admin editor reaches through the
 *   same service.
 */
export const promptsService = {
  listForBook(bookId: string) {
    return apiRequest<PromptView[]>(apiRoutes.prompts.forBook(bookId));
  },
  create(bookId: string, payload: CreatePromptPayload) {
    return apiRequest<PromptView>(apiRoutes.prompts.forBook(bookId), {
      method: 'POST',
      body: payload,
    });
  },
  update(id: string, payload: UpdatePromptPayload) {
    return apiRequest<PromptView>(apiRoutes.prompts.one(id), {
      method: 'PATCH',
      body: payload,
    });
  },
  remove(id: string) {
    return apiRequest<void>(apiRoutes.prompts.one(id), { method: 'DELETE' });
  },
};
